/**
 * What a candidate agrees to: the apply page's consent sentence and the privacy
 * notice it links to. This module is the one place both are written; the
 * Talent Pool's `/privacy` page renders `PRIVACY_NOTICE`, and
 * `docs/talent-privacy-notice.md` is `privacyNoticeMarkdown()` (a test holds the
 * two equal). The browser imports it through `@nemo/core/talent/consent`, so it
 * stays free of node-only imports.
 */
/**
 * Which wording a candidate agreed to. Changed whenever the consent sentence or
 * the notice changes (the retention period included), so a stored consent
 * always names the text it was given to. A second change on one day is
 * that date with `.2`, `.3` and so on.
 */
export const CONSENT_VERSION = '2026-10-27.2';
/** Where the notice is served in Talent Pool, without sign-in. */
export const PRIVACY_PATH = '/privacy';
/** Shown above the notice until Michael has reviewed it. */
export const PRIVACY_NOTICE_STATUS = 'Draft, pending review.';
export const PRIVACY_NOTICE_TITLE = 'Talent Pool privacy notice';
export const PRIVACY_NOTICE = [
    {
        heading: 'Who we are',
        paragraphs: [
            'Project A runs the Talent Pool and decides what happens to what you send it: in data protection terms, Project A is the controller. [Legal entity, address and privacy contact to confirm.]',
        ],
    },
    {
        heading: 'What we hold',
        paragraphs: ['When you send the form, we keep:'],
        bullets: [
            'your name, email address, and, if you give them, your phone number and LinkedIn address;',
            'if you give us your LinkedIn address, the photo from your public LinkedIn profile;',
            'your CV file, and the text we read out of it;',
            'a profile read from your CV: headline, location, years of experience, current employer and title, skills, education, military service, languages and nationality, as far as your CV states them;',
            'your answers to the questionnaire, and your note, if you write one;',
            'when you sent it, and when you agreed to this notice.',
        ],
    },
    {
        heading: 'Why, and on what basis',
        paragraphs: [
            'We hold your profile so that Project A and the companies it backs can find you for roles that fit. We rely on your consent, which you give with the checkbox on the form. You can withdraw it at any time with the link we show you after you send the form; withdrawing does not make what happened before it unlawful.',
        ],
    },
    {
        heading: 'Who sees it',
        paragraphs: [
            'People at Project A, and recruiters at companies Project A backs whom Project A has let in, can search the pool and open your profile once every required question is answered. They can download your CV; a downloaded copy is then also held by that company, under its own responsibility. Every opening of your profile and every download of your CV is recorded, with who did it and when.',
        ],
    },
    {
        heading: 'How your CV is read',
        paragraphs: [
            "A language model reads your CV twice: once on the form, to fill in the answers for you to check, and once after you send it, to write your profile. The model is Google's Gemini, used through Google's paid API. Google may process the request outside the European Union. Under Google's terms for its paid API, what we send is not used to train its models.",
        ],
    },
    {
        heading: 'Where it is kept',
        paragraphs: [
            "Your profile and your CV file are stored on Google Cloud in Frankfurt, Germany. The website reaches you through Cloudflare's network.",
        ],
    },
    {
        heading: 'How long we keep it',
        paragraphs: [
            'We delete your profile and your CV 12 months after you last send them, unless you remove them sooner.',
            'If you remove your profile, we delete your CV, the text read from it and your answers straight away. We keep your name, your email address and the dates you sent it, removed it and agreed to this notice for 12 months after the removal, and then delete those too.',
            'If you ask us to erase everything, we delete it all and keep only a keyed hash of your email address and the date, as the record that we did. Without our key, the hash cannot be matched to your address.',
            'A deleted CV file cannot be recovered. Our database keeps automated backups for 7 days, so a deleted record may remain in a backup for up to 7 days.',
        ],
    },
    {
        heading: 'Your rights',
        paragraphs: [
            'You may ask us for a copy of everything we hold about you, to correct it, to delete it, or to have it in a form you can take elsewhere, and you may withdraw your consent. Your link removes your profile at once. For anything else, or if you have lost the link, write to us at the contact above. You may also complain to a data protection supervisory authority.',
        ],
    },
];
/** The consent sentence beside the checkbox; the last words link to the notice. */
export const CONSENT_SENTENCE = {
    before: 'I agree to Project A storing my CV, my answers and, if I give my LinkedIn address, the photo from my public LinkedIn profile, and sharing them with the companies it backs, as described in the',
    link: 'privacy notice',
    after: '.',
};
/** The notice as Markdown, which is what `docs/talent-privacy-notice.md` holds. */
export function privacyNoticeMarkdown() {
    const lines = [
        `# ${PRIVACY_NOTICE_TITLE}`,
        '',
        `_${PRIVACY_NOTICE_STATUS} Consent version ${CONSENT_VERSION}. Generated from \`packages/core/src/talent/consent.ts\`: edit it there._`,
        '',
    ];
    for (const section of PRIVACY_NOTICE) {
        lines.push(`## ${section.heading}`, '');
        for (const paragraph of section.paragraphs)
            lines.push(paragraph, '');
        if (section.bullets !== undefined) {
            for (const bullet of section.bullets)
                lines.push(`- ${bullet}`);
            lines.push('');
        }
    }
    return lines.join('\n');
}
//# sourceMappingURL=consent.js.map